import { useState, useEffect } from 'react';
import PageWithNavbar from '../components/PageWithNavbar';
import { API_BASE_URL } from '../lib/constants';
import styles from './dashboard.module.css';

export default function Dashboard() {
  const [upcomingDates, setUpcomingDates] = useState<any[]>([]);
  const [recommendations, setRecommendations] = useState<{[key: number]: any}>({});
  const [loadingRecommendations, setLoadingRecommendations] = useState<{[key: number]: boolean}>({});
  const [loadingMoreProducts, setLoadingMoreProducts] = useState<{[key: number]: boolean}>({});
  const token = localStorage.getItem('token');

  const fetchRecommendations = async (personDateId: number) => {
    setLoadingRecommendations(prev => ({ ...prev, [personDateId]: true }));
    try {
      const response = await fetch(`${API_BASE_URL}/api/gift-recommendations/${personDateId}/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        setRecommendations(prev => ({ ...prev, [personDateId]: data }));
      }
    } catch (error) {
      console.error('Error fetching recommendations:', error);
    } finally {
      setLoadingRecommendations(prev => ({ ...prev, [personDateId]: false }));
    }
  };

  const loadMoreProducts = async (personDateId: number) => {
    setLoadingMoreProducts(prev => ({ ...prev, [personDateId]: true }));
    try {
      const response = await fetch(`${API_BASE_URL}/api/gift-recommendations/${personDateId}/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        // Merge additional products with existing ones
        setRecommendations(prev => {
          const current = prev[personDateId];
          if (!current) return prev;
          
          const currentProducts = current.walmart_products || {};
          const additionalProducts = data.additional_walmart_products || {};
          
          // Merge products by category
          const mergedProducts = { ...currentProducts };
          Object.keys(additionalProducts).forEach(category => {
            if (mergedProducts[category]) {
              mergedProducts[category] = [...mergedProducts[category], ...additionalProducts[category]];
            } else {
              mergedProducts[category] = additionalProducts[category];
            }
          });
          
          return {
            ...prev,
            [personDateId]: {
              ...current,
              walmart_products: mergedProducts
            }
          };
        });
      }
    } catch (error) {
      console.error('Error loading more products:', error);
    } finally {
      setLoadingMoreProducts(prev => ({ ...prev, [personDateId]: false }));
    }
  };

  useEffect(() => {
    async function fetchDates() {
              try {
          const response = await fetch(`${API_BASE_URL}/api/people/`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
          
          if (response.ok) {
            const people = await response.json();
            const allDates: any[] = [];
            
            people.forEach((person: any) => {
              if (Array.isArray(person.dates)) {
                person.dates.forEach((date: any) => {
                  if (!date.month) return; // skip if no month
                  
                  // Calculate days until event
                  const today = new Date();
                  const currentYear = today.getFullYear();
                  let eventDate;
                  
                  if (date.day) {
                    eventDate = new Date(currentYear, Number(date.month) - 1, Number(date.day));
                  } else {
                    eventDate = new Date(currentYear, Number(date.month) - 1, 1);
                  }
                  
                  // If the date has passed this year, move to next year
                  if (eventDate < today) {
                    eventDate.setFullYear(currentYear + 1);
                  }
                  
                  const diffTime = eventDate.getTime() - today.getTime();
                  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                  
                  // Only include upcoming dates (within next 90 days)
                  if (diffDays >= 0 && diffDays <= 90) {
                    allDates.push({
                      person,
                      date,
                      diffDays,
                      eventDate
                    });
                  }
                });
              }
            });
            
            // Sort by soonest date
            allDates.sort((a, b) => a.eventDate.getTime() - b.eventDate.getTime());
            const topDates = allDates.slice(0, 10);
            setUpcomingDates(topDates);
            
            // Fetch recommendations for each upcoming date
            topDates.forEach(({ person, date }) => {
              if (date.id && person.preferences && person.preferences.likes && person.preferences.likes.length > 0) {
                fetchRecommendations(date.id);
              }
            });
          }
      } catch (err) {
        setUpcomingDates([]);
      }
    }
    fetchDates();
  }, [token]);

  const renderGiftSection = (_person: any, date: any, recommendation: any, walmartProducts: any, isLoading: boolean) => {
    if (isLoading) {
      return <p className={styles.loadingMessage}>Loading gift ideas...</p>;
    }
    
    if (!recommendation) {
      return (
        <button 
          onClick={() => fetchRecommendations(date.id)}
          className={styles.getIdeasButton}
        >
          Get Gift Ideas
        </button>
      );
    }

    return (
      <div>
        {/* Walmart Products */}
        {Object.keys(walmartProducts).length > 0 && (
          <div>
            <div className={styles.productsGrid}>
              {Object.values(walmartProducts).flat().map((product: any, index: number) => (
                <a 
                  key={`product-${index}`}
                  href={product.productUrl || product.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className={styles.productCard}
                >
                  {/* Product Image */}
                  <div className={styles.productImageContainer}>
                    {product.imageUrl ? (
                      <img 
                        src={product.imageUrl} 
                        alt={product.name}
                        className={styles.productImage}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                          if (nextElement) {
                            nextElement.style.display = 'flex';
                          }
                        }}
                      />
                    ) : null}
                    <div className={styles.productImagePlaceholder} style={{ 
                      display: product.imageUrl ? 'none' : 'flex'
                    }}>
                      🛒
                    </div>
                  </div>
                  
                  {/* Product Info */}
                  <div>
                    {/* Product Name */}
                    <h4 className={styles.productName}>
                      {product.name}
                    </h4>
                    
                    {/* Price */}
                    <div className={styles.productPrice}>
                      ${product.salePrice || product.price}
                    </div>
                    
                    {/* Rating */}
                    {product.customerRating > 0 && (
                      <div className={styles.productRating}>
                        <span className={styles.ratingStar}>⭐</span>
                        <span>{product.customerRating}</span>
                        {product.numReviews > 0 && (
                          <span>({product.numReviews})</span>
                        )}
                      </div>
                    )}
                  </div>
                </a>
              ))}
            </div>
            
            {/* Show More Button */}
            <div className={styles.showMoreContainer}>
              <button 
                onClick={() => loadMoreProducts(date.id)}
                disabled={loadingMoreProducts[date.id]}
                className={styles.showMoreButton}
              >
                {loadingMoreProducts[date.id] ? 'Loading...' : 'Show More Products'}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <PageWithNavbar>
      <section className={styles.container}>
        <h1 className={styles.title}>Dashboard</h1>
        <div className={styles.contentCard}>
          <h2 className={styles.sectionTitle}>Upcoming Dates & Gift Ideas</h2>
          {upcomingDates.length === 0 ? (
            <p className={styles.noDataMessage}>No upcoming dates found.</p>
          ) : (
            <ul className={styles.upcomingList}>
              {upcomingDates.map(({ person, date, diffDays }, idx) => {
                let countdownText = '';
                if (diffDays > 14) {
                  const weeks = Math.ceil(diffDays / 7);
                  countdownText = `${weeks} week${weeks > 1 ? 's' : ''}`;
                } else {
                  countdownText = `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
                }
                
                const hasPreferences = person.preferences && person.preferences.likes && person.preferences.likes.length > 0;
                const isLoading = loadingRecommendations[date.id];
                const recommendationData = recommendations[date.id];
                const recommendation = recommendationData?.recommendations;
                const walmartProducts = recommendationData?.walmart_products || {};
                
                return (
                  <li key={person.id + '-' + date.label + '-' + idx} className={styles.upcomingItem}>
                    <div className={styles.itemHeader}>
                      <strong>{person.name}</strong> — {countdownText} until {date.label || 'Date'}
                    </div>
                    
                    {hasPreferences ? (
                      renderGiftSection(person, date, recommendation, walmartProducts, isLoading)
                    ) : (
                      <p className={styles.noPreferencesMessage}>
                        Add preferences for {person.name} to get gift ideas
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>
    </PageWithNavbar>
  );
}
